import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { RoutedByIndicatorContent } from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  addDays,
  editFormDataAF,
  EditFormMode,
  getSavedStationRouteCacheDurationDays,
  getTrafficTypeTranslationKey,
  IStationRouteDto,
  resolveCurrentStoreRouteCacheDays,
  resolveDirectionsServiceLabel,
  resolveStationRouteDirectionsService,
  trimSeconds,
  useFormats,
  useSystemParams,
} from "@ryde/shared";

import {
  StationRouteBottomBlockStyled,
  StationRouteTabsBottomBlocksStyled,
} from "./station-route.styled";
import { StationRouteMatchingMlPaths } from "./station-route.tabs.matching-ml-paths";

export const StationRouteTabsBottomBlocks: FC = () => {
  const { entity, mode } = useCrudEditContext<IStationRouteDto>();
  // editFormDataAF is untyped (any); annotate local value instead of casting.
  const stationRouteDto: IStationRouteDto | undefined = useRecoilValue(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const systemParams = useSystemParams();
  const { formatDateTimeNormalized, formatDateTimeWithSecondsNormalized } =
    useFormats();
  const { t } = useTranslation();
  const createdAt = formatDateTimeNormalized(stationRouteDto?.createdAt);
  const updatedAt = formatDateTimeNormalized(stationRouteDto?.updatedAt);
  const showUpdatedAt =
    stationRouteDto?.updatedAt &&
    stationRouteDto?.createdAt &&
    trimSeconds(stationRouteDto.updatedAt) !==
      trimSeconds(stationRouteDto.createdAt);
  const hasRouteDate = !!stationRouteDto?.metadata?.routeDate;
  const hasBaseRideDate = !!stationRouteDto?.metadata?.baseRideDate;
  const hasTraffic = !!stationRouteDto?.metadata?.traffic;
  const routedByLabel = resolveDirectionsServiceLabel(
    resolveStationRouteDirectionsService(stationRouteDto?.metadata),
  );

  // Effective TTL / expiry follow current system param (same as BE freshness).
  // Customized ML paths have no cache TTL / expiration — hide both on the form.
  const isCustomizedPath = Boolean(stationRouteDto?.customized);
  const currentCacheDurationDays = resolveCurrentStoreRouteCacheDays(
    systemParams.storeRouteCacheDays,
  );
  const savedCacheDurationDays = getSavedStationRouteCacheDurationDays(
    stationRouteDto?.metadata?.cacheDurationDays,
  );
  const showSavedCacheDurationNote =
    !isCustomizedPath &&
    savedCacheDurationDays !== undefined &&
    savedCacheDurationDays !== currentCacheDurationDays;
  const hasCacheDuration = !isCustomizedPath && currentCacheDurationDays >= 0;

  // Expiry = last cache write (updatedAt) + current retention days.
  const cacheExpiresAt = useMemo(() => {
    if (
      !stationRouteDto?.updatedAt ||
      currentCacheDurationDays <= 0 ||
      stationRouteDto.frozen ||
      isCustomizedPath
    ) {
      return undefined;
    }
    return addDays(stationRouteDto.updatedAt, currentCacheDurationDays);
  }, [
    stationRouteDto?.updatedAt,
    stationRouteDto?.frozen,
    isCustomizedPath,
    currentCacheDurationDays,
  ]);

  return (
    <StationRouteTabsBottomBlocksStyled>
      <StationRouteBottomBlockStyled>
        <label>{t("general.CREATED_AT")}</label>
        <span>{createdAt}</span>
      </StationRouteBottomBlockStyled>
      {cacheExpiresAt && (
        <StationRouteBottomBlockStyled>
          <label>{t("stationRoute.formLabel.cacheExpiresAt")}:</label>
          <span data-e2e="stationRoute-cacheExpiresAt">
            {formatDateTimeWithSecondsNormalized(cacheExpiresAt)}
          </span>
        </StationRouteBottomBlockStyled>
      )}
      {showUpdatedAt && (
        <StationRouteBottomBlockStyled>
          <label>{t("general.UPDATED_AT")}</label>
          <span>{updatedAt}</span>
        </StationRouteBottomBlockStyled>
      )}
      {hasCacheDuration && (
        <StationRouteBottomBlockStyled>
          <label>{t("stationRoute.formLabel.cacheDurationDays")}:</label>
          <span data-e2e="stationRoute-cacheDurationDays-footer">
            {currentCacheDurationDays} {t("general.DAYS")}
            {showSavedCacheDurationNote && (
              <>
                {" "}
                (
                {t("stationRoute.formLabel.daysWhenSaved", {
                  count: savedCacheDurationDays,
                })}
                )
              </>
            )}
          </span>
        </StationRouteBottomBlockStyled>
      )}
      {hasTraffic && (
        <StationRouteBottomBlockStyled>
          <label>{t("stationRoute.formLabel.traffic")}:</label>
          <span data-e2e="stationRoute-traffic">
            {t(
              getTrafficTypeTranslationKey(stationRouteDto?.metadata?.traffic),
            )}
          </span>
        </StationRouteBottomBlockStyled>
      )}
      {hasRouteDate && (
        <StationRouteBottomBlockStyled>
          <label>{t("stationRoute.formLabel.routeDate")}:</label>
          <span>
            {formatDateTimeNormalized(stationRouteDto?.metadata?.routeDate)}
          </span>
        </StationRouteBottomBlockStyled>
      )}
      {hasBaseRideDate && (
        <StationRouteBottomBlockStyled>
          <label>{t("stationRoute.formLabel.baseRideDate")}:</label>
          <span>
            {formatDateTimeNormalized(stationRouteDto?.metadata?.baseRideDate)}
          </span>
        </StationRouteBottomBlockStyled>
      )}
      {!!routedByLabel && (
        <StationRouteBottomBlockStyled>
          <RoutedByIndicatorContent
            directionsService={resolveStationRouteDirectionsService(
              stationRouteDto?.metadata,
            )}
          />
        </StationRouteBottomBlockStyled>
      )}
      {Boolean(stationRouteDto?.optimized) && (
        <StationRouteBottomBlockStyled>
          <span data-e2e="stationRoute-optimized">
            <span className="station-route-bottom-optimize-icon" aria-hidden>
              ⚡
            </span>
            {t("stationRoute.formLabel.optimized")}
          </span>
        </StationRouteBottomBlockStyled>
      )}
      {Boolean(stationRouteDto?.metadata?.savedInOsrmFirstFlow) && (
        <StationRouteBottomBlockStyled>
          <span data-e2e="stationRoute-savedInOsrmFirstFlow">
            <span className="station-route-bottom-optimize-icon" aria-hidden>
              📍
            </span>
            {t("stationRoute.formLabel.savedInOsrmFirstFlow")}
          </span>
        </StationRouteBottomBlockStyled>
      )}
      <StationRouteMatchingMlPaths />
    </StationRouteTabsBottomBlocksStyled>
  );
};
